import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { useClassNames } from "@/hooks/useClassNames";
import { useLayout } from "@/contexts/LayoutContext";
import { MenuItem } from "@/types/domain";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/shadcn/dropdown-menu";
import { SearchBar } from "../SearchBar";
import { MenuItemRow } from "./MenuItemRow";

const findOpenItemId = (items: MenuItem[]): string | null => {
  for (const item of items) {
    if (item.children?.some((c) => c.isSelected)) return item.id;
  }
  return null;
}

const SKELETON_COUNTS = [8, 3, 5, 4, 6, 7, 9, 4, 5, 3, 7, 6];

const MenuSkeleton = ({ isMini }: { isMini: boolean }) => (
  <ul className={`py-2 mt-2 space-y-0.5 ${!isMini ? "pl-2.5" : ""}`} aria-hidden="true">
    {SKELETON_COUNTS.map((width, i) => (
      <li key={i} className={`flex items-center gap-2.5 px-2 py-2 rounded ${isMini ? "justify-center" : ""}`}>
        <div className="animate-pulse rounded bg-white/20 shrink-0" style={{ width: 18, height: 18 }} />
        {!isMini && <div className="animate-pulse rounded bg-white/20 h-3.5" style={{ width: `${width * 18}px` }} />}
      </li>
    ))}
  </ul>
);

export const SideNav = ({ menuItems, menuLoading, menuError }: { menuItems: MenuItem[]; menuLoading: boolean; menuError: boolean }) => {
  const { sideNavState, mobileNavOpen, data } = useLayout();
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  // On mobile (drawer open), always treat as wide so labels are visible
  const isMini = sideNavState === "mini" && !mobileNavOpen;
  const routes = data.appRoutes;

  const handleToggleItem = (id: string) => {
    setOpenItemId((prev) => (prev === id ? null : id));
  }

  useEffect(() => {
    const parentId = findOpenItemId(menuItems);
    if (parentId) setOpenItemId(parentId);
  }, [menuItems]);

  const firstName = data.username.split(' ')[0];

  return (
    <nav
      role="navigation"
      className="layout-side-nav"
      aria-label="Main navigation"
    >
      <ScrollArea className="flex-1 min-h-0">
        {/* Mobile/tablet-portrait search — hidden on md+ (lives in TopBar there) */}
        <div className="md:hidden border-b border-white/20">
          <SearchBar variant="stacked" />
        </div>
        {menuError ? (
          <p className="px-4 py-4 text-sm text-white/60">Navigation failed to load.</p>
        ) : menuLoading ? (
          <MenuSkeleton isMini={isMini} />
        ) : (
          <ul className={useClassNames("py-2 mt-2 space-y-0.5", !isMini && "pl-2.5")}>
            {menuItems.map((item) => (
              <MenuItemRow key={item.id} item={item} isMini={isMini} openItemId={openItemId} onToggleItem={handleToggleItem} />
            ))}
          </ul>
        )}
      </ScrollArea>

      {/* User avatar footer — only on very small mobile (<375px) replacing top-bar avatar */}
      {isMini ? (
        <div className="flex justify-center px-2 py-3 border-t border-white/10 shrink-0" style={{ backgroundColor: "var(--layout-side-nav-bg-light)" }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center justify-center w-7 h-7 rounded-full font-semibold text-white shrink-0 select-none hover:opacity-85 transition-opacity focus:outline-none"
                style={{ backgroundColor: data.userAvatarColor, fontSize: 11 }}
                aria-label={data.username}
              >
                {data.userInitials}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end">
              <DropdownMenuLabel title={data.username}>{firstName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href={routes.logout}>
                  <FontAwesomeIcon icon={faArrowRightFromBracket} className="mr-2" />
                  Logout
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="flex items-center px-4 py-3 border-t border-white/10 shrink-0" style={{ backgroundColor: "var(--layout-side-nav-bg-light)" }}>
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full font-semibold text-white shrink-0 select-none"
            style={{ backgroundColor: data.userAvatarColor, fontSize: 11 }}
            aria-hidden="true"
          >
            {data.userInitials}
          </div>
          <span className="flex-1 ml-2.5 text-sm text-white overflow-hidden text-ellipsis whitespace-nowrap text-center" title={data.username}>{firstName}</span>
          <a
            href={routes.logout}
            className="flex items-center gap-1.5 ml-2 py-1 text-xs text-white"
            aria-label="Logout"
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} size="xs" />
            Logout
          </a>
        </div>
      )}
    </nav>
  );
}
